import { useRef, useState } from "react";
import { toast } from "sonner";
import { UserCog, Receipt, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const OPEN_AT = 60;
const isPhone = () => window.matchMedia("(max-width: 767px)").matches;

export const AssignDialog = ({ study, staff, onClose, onAssigned }) => {
  const save = async (v) => {
    try { const r = await api.put(`/studies/${study.id}`, { assigned_to: v === "none" ? "" : v }); onAssigned(r.data); toast.success("Study assigned"); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!study} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-sm" data-testid="swipe-assign-dialog">
        <DialogHeader className="text-left"><DialogTitle className="font-display">Assign study</DialogTitle><DialogDescription className="truncate">{study?.input.project_name}</DialogDescription></DialogHeader>
        <Select value={study?.assigned_to || "none"} onValueChange={save}>
          <SelectTrigger data-testid="swipe-assign-select" className="h-11 bg-slate-950/60 border-slate-700"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="none">Unassigned</SelectItem>
            {staff.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`swipe-assign-option-${s.id}`}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </DialogContent>
    </Dialog>
  );
};

const Action = ({ icon: Icon, label, tone, onClick, href, testId }) => {
  const cls = `w-[72px] h-full flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ${tone}`;
  return href ? <a href={href} data-testid={testId} className={cls}><Icon className="h-5 w-5" />{label}</a>
    : <button type="button" data-testid={testId} onClick={onClick} className={cls}><Icon className="h-5 w-5" />{label}</button>;
};

export const SwipeRow = ({ id, actions, bg = "bg-[#090d16]", rounded = "rounded-xl", children }) => {
  const width = actions.length * 72;
  const [x, setX] = useState(0);
  const t = useRef(null);
  const start = (e) => { if (!isPhone() || !actions.length) return; t.current = { x0: e.touches[0].clientX, y0: e.touches[0].clientY, base: x, dir: null }; };
  const move = (e) => {
    const s = t.current;
    if (!s) return;
    const dx = e.touches[0].clientX - s.x0, dy = e.touches[0].clientY - s.y0;
    if (!s.dir && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) s.dir = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    if (s.dir === "x") setX(Math.max(-width, Math.min(0, s.base + dx)));
  };
  const end = () => { const s = t.current; t.current = null; if (s?.dir === "x") setX((v) => (v < -OPEN_AT ? -width : 0)); };
  const close = () => setX(0);
  const guardClick = (e) => { if (x !== 0) { e.preventDefault(); e.stopPropagation(); close(); } };
  return (
    <div className={`relative overflow-hidden ${rounded}`} data-testid={`swipe-row-${id}`}>
      <div className="md:hidden absolute inset-y-0 right-0 flex" style={{ width }} aria-hidden={x === 0}>
        {actions.map((a) => <Action key={a.testId} {...a} onClick={a.onClick && (() => { close(); a.onClick(); })} />)}
      </div>
      <div onTouchStart={start} onTouchMove={move} onTouchEnd={end} onClickCapture={guardClick} data-testid={`swipe-card-${id}`}
        className={`relative touch-pan-y ${rounded} ${bg}`} style={{ transform: `translateX(${x}px)`, transition: t.current ? "none" : "transform 200ms ease-out" }}>
        {children}
      </div>
    </div>
  );
};

export const SwipeActions = ({ study, canAssign, onAssign, onDelete, children }) => {
  const navigate = useNavigate();
  const actions = [
    canAssign && { icon: UserCog, label: "Assign", tone: "bg-sky-600 text-white", testId: `swipe-assign-${study.id}`, onClick: () => onAssign(study) },
    { icon: Receipt, label: "Invoice", tone: "bg-emerald-600 text-white", testId: `swipe-invoice-${study.id}`, onClick: () => navigate(`/invoices?study=${study.id}`) },
    { icon: Trash2, label: "Delete", tone: "bg-rose-600 text-white", testId: `swipe-delete-${study.id}`, onClick: () => onDelete(study.id) },
  ].filter(Boolean);
  return <SwipeRow id={study.id} actions={actions}>{children}</SwipeRow>;
};
